import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { AppRole } from "@msc/shared";

import { api } from "../lib/api.js";

export interface UserRoleEntry {
  id: string;
  role: AppRole;
  segmentId: string | null;
  grantedAt: string;
}

export interface UserListItem {
  id: string;
  email: string;
  fullName: string;
  active: boolean;
  roles: UserRoleEntry[];
  /**
   * Bereiche, in denen die Person Service Lead ist. Grundlage der abgeleiteten
   * Rolle „Projektleiter" — sie steht nicht in der Rollentabelle, sondern
   * entsteht aus den Beauftragungen und ist deshalb nicht entziehbar.
   */
  leadSegmentIds: string[];
}

export const userKeys = {
  all: ["users"] as const,
  list: () => [...userKeys.all, "list"] as const,
};

export function useUsers() {
  return useQuery<UserListItem[]>({
    queryKey: userKeys.list(),
    queryFn: () => api.get<UserListItem[]>("/users"),
  });
}

export interface GrantRolePayload {
  employeeId: string;
  role: AppRole;
  segmentId?: string;
}

export function useGrantRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GrantRolePayload) =>
      api.post(`/users/${payload.employeeId}/roles`, {
        role: payload.role,
        segmentId: payload.segmentId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useRevokeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { employeeId: string; roleId: string }) =>
      api.delete(`/users/${payload.employeeId}/roles/${payload.roleId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { employeeId: string; active: boolean }) =>
      api.patch(`/users/${payload.employeeId}`, { active: payload.active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useSetUserName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { employeeId: string; fullName: string }) =>
      api.patch(`/users/${payload.employeeId}`, { fullName: payload.fullName }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Jeder authentifizierte Nutzer darf seinen eigenen Anzeigenamen ändern.
 * Active / Rollen bleiben admin-only.
 */
export function useSetOwnName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { fullName: string }) =>
      api.patch("/users/me", { fullName: payload.fullName }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

/** ------------------------------------------- Mitarbeiter-Stammdaten (S2) */

export interface EmployeeProfile {
  careerLevel: string | null;
  federalState: string;
  hoursPerDay: string;
  segmentId: string | null;
  mentorEmployeeId: string | null;
  yppDate: string | null;
}

/** Mitarbeiter ohne Login anlegen (Berater/Externe) — idempotent per E-Mail. */
export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; fullName: string }) =>
      api.post<{ id: string; created: boolean }>("/users/employees", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userKeys.all });
      void qc.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

export function useEmployeeProfile(employeeId: string | null) {
  return useQuery<EmployeeProfile>({
    queryKey: ["employeeProfile", employeeId],
    queryFn: () =>
      api.get<EmployeeProfile>(`/users/employees/${employeeId}/profile`),
    enabled: employeeId !== null,
  });
}

export function useSaveEmployeeProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      ...payload
    }: {
      employeeId: string;
      careerLevel: string | null;
      federalState: string;
      hoursPerDay: number;
      segmentId: string | null;
      mentorEmployeeId: string | null;
      yppDate: string | null;
    }) => api.put<void>(`/users/employees/${employeeId}/profile`, payload),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["employeeProfile", vars.employeeId] });
      // Cockpit komplett mitziehen: Team-Liste, KPI-Kapazität, Planungsbasis.
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

/** ---------------------------------------------- Stunden-Zeiträume (S2) */

export interface HoursPeriod {
  id: string;
  validFrom: string;
  validTo: string | null;
  hoursPerDay: number;
}

export function useHoursPeriods(employeeId: string | null) {
  return useQuery<HoursPeriod[]>({
    queryKey: ["hoursPeriods", employeeId],
    queryFn: () =>
      api.get<HoursPeriod[]>(`/users/employees/${employeeId}/hours-periods`),
    enabled: employeeId !== null,
  });
}

export function useAddHoursPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      ...payload
    }: {
      employeeId: string;
      validFrom: string;
      validTo: string | null;
      hoursPerDay: number;
    }) =>
      api.post<{ id: string }>(
        `/users/employees/${employeeId}/hours-periods`,
        payload,
      ),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["hoursPeriods", vars.employeeId] });
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}

export function useDeleteHoursPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      periodId,
    }: {
      employeeId: string;
      periodId: string;
    }) =>
      api.delete<void>(
        `/users/employees/${employeeId}/hours-periods/${periodId}`,
      ),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ["hoursPeriods", vars.employeeId] });
      void qc.invalidateQueries({ queryKey: ["kpi"] });
    },
  });
}
